import { useEffect, useRef } from 'react';

const N   = 14;
const SZ  = 36;   // CSS px — matches h-9 w-9
const R   = 11;   // sphere radius in canvas units
const P   = 60;   // perspective depth
const CLR = '#94a3b8'; // slate-400, same hue as the icon

// Fibonacci sphere: evenly distributed N points on a unit sphere scaled to r
const buildGlobe = (n, r) => {
  const golden = Math.PI * (3 - Math.sqrt(5));
  return Array.from({ length: n }, (_, i) => {
    const y   = 1 - (i / (n - 1)) * 2;
    const rad = Math.sqrt(Math.max(0, 1 - y * y));
    const th  = golden * i;
    return { x: Math.cos(th) * rad * r, y: y * r, z: Math.sin(th) * rad * r };
  });
};

const GLOBE_PTS = buildGlobe(N, R);

const MiniSentry = ({ active }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width  = SZ * dpr;
    canvas.height = SZ * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // Each particle: physics state + home position on the sphere
    const ps = GLOBE_PTS.map(g => ({
      x: g.x, y: g.y, z: g.z,
      vx: 0,  vy: 0,  vz: 0,
      gx: g.x, gy: g.y, gz: g.z,
      ns:   [Math.random() * 100, Math.random() * 100, Math.random() * 100],
      size: 1.1 + Math.random() * 0.6,
    }));

    let rotY    = 0;
    let frame   = 0;
    let gs      = 1.0;   // globe strength: 1=sphere formation, 0=organic life
    let targetG = 1.0;
    // Start in globe mode for a while so users can recognise the icon → organism morph
    let modeEnd = performance.now() + 11000 + Math.random() * 7000;
    let raf     = null;

    const tick = (now) => {
      raf = requestAnimationFrame(tick);
      frame++;

      // Switch modes on timer
      if (now >= modeEnd) {
        if (targetG === 1) {
          targetG = 0;
          modeEnd = now + 3000 + Math.random() * 4000;   // alive 3-7 s
        } else {
          targetG = 1;
          modeEnd = now + 10000 + Math.random() * 8000;  // globe 10-18 s
        }
      }

      // Smooth lerp between modes
      gs   += (targetG - gs) * 0.020;
      // Slight extra spin when alive
      rotY += 0.003 + (1 - gs) * 0.005;

      const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
      const cx   = SZ / 2,        cy   = SZ / 2;
      const t    = frame * 0.007;

      for (const p of ps) {
        // Pull toward sphere home position — stronger homing so globe reforms cleanly
        p.vx += (p.gx - p.x) * 0.20 * gs;
        p.vy += (p.gy - p.y) * 0.20 * gs;
        p.vz += (p.gz - p.z) * 0.20 * gs;

        // Organic sine-noise drift when alive
        if (gs < 0.95) {
          const a = 1 - gs;
          p.vx += Math.sin(t * 1.30 + p.ns[0]) * 0.032 * a;
          p.vy += Math.sin(t * 0.91 + p.ns[1]) * 0.032 * a;
          p.vz += Math.sin(t * 1.67 + p.ns[2]) * 0.032 * a;
          // Soft containment boundary
          const d = Math.sqrt(p.x*p.x + p.y*p.y + p.z*p.z);
          if (d > R * 1.4) {
            p.vx -= p.x * 0.012;
            p.vy -= p.y * 0.012;
            p.vz -= p.z * 0.012;
          }
        }

        const fr = 0.83 + gs * 0.14;   // 0.83 alive → 0.97 globe (high friction = no oscillation)
        p.vx *= fr; p.vy *= fr; p.vz *= fr;
        p.x  += p.vx; p.y += p.vy; p.z += p.vz;
      }

      // Y-axis rotation projection + depth sort (painter's algorithm)
      const pts = ps.map(p => {
        const rx  =  p.x * cosY + p.z * sinY;
        const rz  = -p.x * sinY + p.z * cosY;
        const sc  = P / (P + rz);
        return { x: rx * sc + cx, y: p.y * sc + cy, sc, rz, size: p.size };
      });
      pts.sort((a, b) => b.rz - a.rz); // farthest first

      ctx.clearRect(0, 0, SZ, SZ);

      // Synapses
      ctx.strokeStyle = CLR;
      ctx.lineWidth   = 0.3;
      const maxD  = 12;
      const maxD2 = maxD * maxD;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx  = pts[i].x - pts[j].x;
          const dy  = pts[i].y - pts[j].y;
          const d2  = dx*dx + dy*dy;
          if (d2 < maxD2) {
            ctx.globalAlpha = (1 - Math.sqrt(d2) / maxD) * 0.40 * Math.min(pts[i].sc, pts[j].sc);
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }

      // Nodes
      ctx.fillStyle = CLR;
      for (const p of pts) {
        ctx.globalAlpha = Math.min(0.88, p.sc * 0.76);
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * p.sc * 1.05, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    };

    raf = requestAnimationFrame(tick);
    return () => { if (raf !== null) cancelAnimationFrame(raf); };
  }, [active]);

  if (!active) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{ width: SZ, height: SZ, display: 'block' }}
    />
  );
};

export default MiniSentry;
